import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CircleAlert, GripVertical, ImagePlus, RotateCw, Trash2, Type, X } from 'lucide-react'
import { cn } from './cn'
import { Popover } from './menu'
import { Button } from './button'
import { Textarea } from './input'
import { toast } from './toast'
import { ApiError, getStoreId } from '@/lib/api'
import { bytes } from '@/lib/format'
import type { FileDto } from '@shared/api'

export interface UploadedImage { id: string; url: string; alt?: string; width?: number | null; height?: number | null }
export type UploadFn = (file: File, opts: { onProgress: (ratio: number) => void; signal: AbortSignal }) => Promise<UploadedImage>

/**
 * Upload one file to the files module with progress (XHR, same headers as lib/api.ts).
 * Returns the FileDto. Throws ApiError on failure. `path` defaults to POST /api/admin/files.
 */
export function uploadFile(file: File, opts: { onProgress?: (ratio: number) => void; signal?: AbortSignal; path?: string; fields?: Record<string, string> } = {}): Promise<FileDto> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', opts.path ?? '/api/admin/files')
    xhr.withCredentials = true
    xhr.setRequestHeader('X-Requested-With', 'fetch')
    xhr.setRequestHeader('Accept', 'application/json')
    const sid = getStoreId()
    if (sid) xhr.setRequestHeader('X-Store-Id', sid)
    xhr.upload.onprogress = e => { if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total) }
    xhr.onload = () => {
      let body: any = null
      try { body = JSON.parse(xhr.responseText) } catch { /* not json */ }
      if (xhr.status >= 200 && xhr.status < 300 && body && typeof body === 'object') return resolve(body as FileDto)
      const e = body?.error
      reject(new ApiError(xhr.status, e?.code ?? 'upload_failed', e?.message ?? (xhr.status === 413 ? 'That file is too large.' : `Upload failed (${xhr.status}).`), e?.fields))
    }
    xhr.onerror = () => reject(new ApiError(0, 'network', 'Upload failed — check your connection.'))
    xhr.onabort = () => reject(new ApiError(0, 'aborted', 'Upload cancelled.'))
    opts.signal?.addEventListener('abort', () => xhr.abort())
    const fd = new FormData()
    fd.append('file', file)
    for (const [k, v] of Object.entries(opts.fields ?? {})) fd.append(k, v)
    xhr.send(fd)
  })
}

/** Default upload: the files module; picks the 800 px variant for display when present. */
export const defaultImageUpload: UploadFn = async (file, { onProgress, signal }) => {
  const f = await uploadFile(file, { onProgress, signal })
  const url = f.variants?.['800'] ?? f.url ?? Object.values(f.variants ?? {})[0]
  if (!f.id || !url) throw new ApiError(500, 'bad_response', 'The server did not return the uploaded image.')
  return { id: f.id, url, alt: f.alt ?? '', width: f.width, height: f.height }
}

interface Pending { key: string; file: File; preview: string; progress: number; error: string | null; ctrl: AbortController }

export interface ImageUploaderProps {
  value: UploadedImage[]
  onChange: (images: UploadedImage[]) => void
  upload?: UploadFn
  /** Max images (default 50). */
  max?: number
  /** Max file size in bytes (default 20 MB). */
  maxSize?: number
  accept?: string
  /** Also accept images pasted anywhere on the page (when focus isn't in a text field). */
  globalPaste?: boolean
  /** Allow editing alt text (default true). */
  altText?: boolean
  disabled?: boolean
  className?: string
  /** Text inside the empty drop zone. */
  hint?: ReactNode
}

/**
 * Media grid: drag & drop, click, or paste images; per-file progress with cancel/retry; drag (or
 * keyboard) to reorder — the first image is the main one; alt text; remove.
 * `<ImageUploader value={images} onChange={setImages} />`
 */
export function ImageUploader({ value, onChange, upload = defaultImageUpload, max = 50, maxSize = 20 * 1024 * 1024, accept = 'image/jpeg,image/png,image/webp,image/gif,image/avif', globalPaste, altText = true, disabled, className, hint }: ImageUploaderProps) {
  const [pending, setPendingState] = useState<Pending[]>([])
  const [over, setOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // The latest images, including ones reported through onChange that the parent hasn't rendered
  // back yet: uploads finishing in the same tick must each append to the previous result, not to
  // the value of the last render (which would drop all but one of them).
  const valueRef = useRef(value)
  const lastProp = useRef(value)
  if (value !== lastProp.current) { lastProp.current = value; valueRef.current = value }
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const commit = useCallback((next: UploadedImage[]) => { valueRef.current = next; onChangeRef.current(next) }, [])
  // Pending uploads live in a ref too, so unmount can abort the ones still in flight.
  const pendingRef = useRef<Pending[]>([])
  const setPending = useCallback((fn: (list: Pending[]) => Pending[]) => {
    pendingRef.current = fn(pendingRef.current)
    setPendingState(pendingRef.current)
  }, [])
  const mounted = useRef(true)
  const acceptList = accept.split(',').map(s => s.trim())

  const start = useCallback((p: Pending) => {
    upload(p.file, {
      signal: p.ctrl.signal,
      onProgress: r => { if (mounted.current) setPending(list => list.map(x => (x.key === p.key ? { ...x, progress: r } : x))) },
    }).then(img => {
      if (!mounted.current || p.ctrl.signal.aborted) return
      setPending(list => list.filter(x => x.key !== p.key))
      URL.revokeObjectURL(p.preview)
      if (!valueRef.current.some(v => v.id === img.id)) commit([...valueRef.current, img])
    }).catch(err => {
      if (!mounted.current || (err instanceof ApiError && err.code === 'aborted')) return
      setPending(list => list.map(x => (x.key === p.key ? { ...x, error: err instanceof Error ? err.message : 'Upload failed' } : x)))
    })
  }, [upload, commit, setPending])

  const addFiles = useCallback((files: File[]) => {
    if (disabled) return
    const room = max - valueRef.current.length - pendingRef.current.length
    const ok: File[] = []
    for (const f of files) {
      if (!acceptList.some(a => a === f.type || (a.endsWith('/*') && f.type.startsWith(a.slice(0, -1))))) { toast.error(`${f.name}: that file type isn’t supported.`); continue }
      if (f.size > maxSize) { toast.error(`${f.name} is ${bytes(f.size)} — the limit is ${bytes(maxSize)}.`); continue }
      ok.push(f)
    }
    if (ok.length > room) toast.warning(`Only ${Math.max(0, room)} more image${room === 1 ? '' : 's'} can be added.`)
    const next = ok.slice(0, Math.max(0, room)).map(file => ({ key: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file, preview: URL.createObjectURL(file), progress: 0, error: null, ctrl: new AbortController() }))
    if (!next.length) return
    setPending(list => [...list, ...next])
    next.forEach(start)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, max, maxSize, start, accept, setPending])

  // Leaving the page (or unmounting the uploader) cancels uploads still in flight and frees previews.
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      for (const p of pendingRef.current) { p.ctrl.abort(); URL.revokeObjectURL(p.preview) }
      pendingRef.current = []
    }
  }, [])

  useEffect(() => {
    if (!globalPaste || disabled) return
    const onPaste = (e: ClipboardEvent) => {
      const t = document.activeElement as HTMLElement | null
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA'].includes(t.tagName))) return
      const files = Array.from(e.clipboardData?.files ?? []).filter(f => f.type.startsWith('image/'))
      if (files.length) { e.preventDefault(); addFiles(files) }
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [globalPaste, disabled, addFiles])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }))
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return
    const cur = valueRef.current
    const from = cur.findIndex(i => i.id === e.active.id), to = cur.findIndex(i => i.id === e.over!.id)
    if (from >= 0 && to >= 0) commit(arrayMove(cur, from, to))
  }
  const retry = (p: Pending) => {
    const fresh = { ...p, error: null, progress: 0, ctrl: new AbortController() }
    setPending(list => list.map(x => (x.key === p.key ? fresh : x)))
    start(fresh)
  }
  const cancel = (p: Pending) => { p.ctrl.abort(); URL.revokeObjectURL(p.preview); setPending(list => list.filter(x => x.key !== p.key)) }

  const empty = value.length === 0 && pending.length === 0
  const dropHandlers = {
    onDragOver: (e: React.DragEvent) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setOver(true) } },
    onDragLeave: (e: React.DragEvent) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false) },
    onDrop: (e: React.DragEvent) => { if (e.dataTransfer.files.length) { e.preventDefault(); setOver(false); addFiles(Array.from(e.dataTransfer.files)) } },
    onPaste: (e: React.ClipboardEvent) => { const files = Array.from(e.clipboardData.files).filter(f => f.type.startsWith('image/')); if (files.length) { e.preventDefault(); addFiles(files) } },
  }
  const full = value.length + pending.length >= max

  return (
    <div className={cn('relative', className)} {...dropHandlers}>
      <input ref={inputRef} type="file" accept={accept} multiple hidden onChange={e => { addFiles(Array.from(e.target.files ?? [])); e.target.value = '' }} />
      {empty ? (
        <button type="button" disabled={disabled} onClick={() => inputRef.current?.click()}
          className={cn('flex w-full flex-col items-center justify-center gap-2 rounded-card border border-dashed border-border-strong bg-surface-sunken/50 px-6 py-10 text-center transition-colors',
            'hover:border-primary/60 hover:bg-primary-soft/30 focus-visible:outline-2 focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-60',
            over && 'border-primary bg-primary-soft/50')}>
          <span className="grid size-10 place-items-center rounded-full bg-surface text-text-muted shadow-card"><ImagePlus className="size-5" /></span>
          <span className="text-base font-medium text-text">{over ? 'Drop to upload' : 'Add images'}</span>
          <span className="text-sm text-text-muted">{hint ?? <>Drag & drop, paste, or click to browse · JPG, PNG, WebP up to {bytes(maxSize)}</>}</span>
        </button>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={value.map(v => v.id)} strategy={rectSortingStrategy}>
            <div className={cn('grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5', over && 'rounded-card outline-2 outline-offset-4 outline-dashed outline-primary')}>
              {value.map((img, i) => (
                <SortableTile key={img.id} img={img} main={i === 0} large={i === 0 && value.length + pending.length - 1 + (full || disabled ? 0 : 1) >= 6} disabled={disabled} altText={altText}
                  onRemove={() => commit(valueRef.current.filter(v => v.id !== img.id))}
                  onAlt={alt => commit(valueRef.current.map(v => (v.id === img.id ? { ...v, alt } : v)))} />
              ))}
              {pending.map(p => (
                <div key={p.key} className="relative aspect-square overflow-hidden rounded-card border border-border bg-surface-sunken">
                  <img src={p.preview} alt="" className={cn('size-full object-cover', p.error ? 'opacity-30 grayscale' : 'opacity-60')} />
                  {p.error ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-2 text-center">
                      <CircleAlert className="size-4 text-danger-strong" />
                      <span className="line-clamp-2 text-xs font-medium text-danger-strong">{p.error}</span>
                      <div className="flex gap-1">
                        <Button size="sm" icon={<RotateCw />} tooltip="Retry" onClick={() => retry(p)} />
                        <Button size="sm" icon={<X />} tooltip="Remove" onClick={() => cancel(p)} />
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-x-2 bottom-2 flex items-center gap-2">
                      <div className="h-1 flex-1 overflow-hidden rounded-full bg-black/20"><div className="h-full rounded-full bg-primary transition-[width] duration-150" style={{ width: `${Math.round(p.progress * 100)}%` }} /></div>
                      <button type="button" aria-label="Cancel upload" onClick={() => cancel(p)} className="grid size-5 place-items-center rounded-full bg-black/50 text-white hover:bg-black/70"><X className="size-3" /></button>
                    </div>
                  )}
                </div>
              ))}
              {!full && !disabled && (
                <button type="button" onClick={() => inputRef.current?.click()} aria-label="Add images"
                  className="grid aspect-square place-items-center rounded-card border border-dashed border-border-strong text-text-subtle transition-colors hover:border-primary/60 hover:bg-primary-soft/30 hover:text-primary-strong focus-visible:outline-2 focus-visible:outline-focus">
                  <ImagePlus className="size-5" />
                </button>
              )}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  )
}

function SortableTile({ img, main, large, disabled, altText, onRemove, onAlt }: { img: UploadedImage; main: boolean; large: boolean; disabled?: boolean; altText: boolean; onRemove: () => void; onAlt: (alt: string) => void }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: img.id, disabled })
  const [alt, setAlt] = useState(img.alt ?? '')
  return (
    <div ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : undefined, zIndex: isDragging ? 10 : undefined }}
      className={cn('group/tile relative aspect-square overflow-hidden rounded-card border border-border bg-surface-sunken', large && 'col-span-2 row-span-2')}>
      <img src={img.url} alt={img.alt ?? ''} loading="lazy" decoding="async" className="size-full object-cover" draggable={false} />
      {main && <span className="absolute bottom-2 left-2 flex h-5 items-center rounded-md bg-black/60 px-2 text-xs font-medium text-white backdrop-blur-sm">Main</span>}
      {!disabled && (
        <div className="absolute inset-0 flex items-start justify-end gap-1 bg-gradient-to-b from-black/35 via-transparent to-transparent p-2 opacity-0 transition-opacity duration-150 group-hover/tile:opacity-100 group-focus-within/tile:opacity-100 [@media(hover:none)]:opacity-100">
          <button ref={setActivatorNodeRef} type="button" aria-label="Drag to reorder" {...attributes} {...listeners}
            className="mr-auto grid size-7 cursor-grab touch-none place-items-center rounded-md bg-black/55 text-white hover:bg-black/75 active:cursor-grabbing focus-visible:outline-2 focus-visible:outline-white"><GripVertical className="size-4" /></button>
          {altText && (
            <Popover className="w-72" onOpenChange={o => { if (o) setAlt(img.alt ?? ''); else if (alt.trim() !== alt) { setAlt(alt.trim()); onAlt(alt.trim()) } }}
              trigger={<button type="button" aria-label="Edit alt text" className={cn('grid size-7 place-items-center rounded-md bg-black/55 text-white hover:bg-black/75 focus-visible:outline-2 focus-visible:outline-white', img.alt && 'ring-1 ring-white/60')}><Type className="size-3.5" /></button>}>
              <div className="flex flex-col gap-2">
                <label htmlFor={`alt-${img.id}`} className="text-sm font-medium">Alt text</label>
                <Textarea id={`alt-${img.id}`} minRows={2} maxRows={5} maxLength={250} value={alt} onChange={e => { setAlt(e.target.value); onAlt(e.target.value) }} placeholder="Describe the image for shoppers using screen readers and for search engines" />
                <span className="text-xs text-text-subtle">{alt.length}/250</span>
              </div>
            </Popover>
          )}
          <button type="button" aria-label="Remove image" onClick={onRemove}
            className="grid size-7 place-items-center rounded-md bg-black/55 text-white hover:bg-danger focus-visible:outline-2 focus-visible:outline-white"><Trash2 className="size-3.5" /></button>
        </div>
      )}
    </div>
  )
}
